/**
 * Splits a collection into pages of exactly the configured card count, then
 * the Next Page tile. Cards are products, the image featured tile and live
 * promotion banners.
 *
 * Shopify's {% paginate %} slices the raw collection, but the grid skips variant
 * child products (custom.hide_on_plp) while rendering, so a raw page of 47 can
 * produce far fewer tiles — /collections/bourjois renders 6. Liquid cannot look
 * past its own page (paginate caps at 50 per page), so the split happens here.
 *
 * sections/collection-product-index.liquid lists the visible product IDs of each
 * raw page. Read in order, they give the whole collection as the customer sees
 * it, which is cut into pages of pageSize cards around the banners' slots
 * (see layoutPages). A page is addressed by the raw page
 * its first product sits on plus how many visible products of that raw page
 * came before it: ?page=5&skip=12. The server renders raw page 5, so the URL
 * still lands on the right products without JS, and this component trims the
 * leading tiles and tops the page up from the raw pages that follow.
 *
 * The Next Page tile and the numbered pagination are then pointed at those
 * pages, so the numbers match what is on screen.
 */
const INDEX_SECTION_ID = 'collection-product-index';
const INDEX_CACHE_PREFIX = 'collection-product-index:';
const INDEX_CACHE_TTL = 10 * 60 * 1000;
const INDEX_FETCH_CONCURRENCY = 4;
const PAGINATION_WINDOW = 2;

class CollectionGridFill extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('[collection-items-wrapper]');
    if (!this.wrapper) return;

    // The filtering script replaces the wrapper's innerHTML rather than
    // re-rendering this element, so the new grid needs filling too. Only that
    // swap touches the wrapper's own children; a fill changes nodes below it.
    this.observer = new MutationObserver(() => this.fill());
    this.observer.observe(this.wrapper, { childList: true });

    this.fill();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }

  async fill() {
    const meta = this.wrapper.querySelector('[collection-grid-meta]');
    const grid = this.wrapper.querySelector('.collection-listing__items');
    if (!meta || !grid || meta.dataset.filled === 'true') return;

    const pageSize = parseInt(meta.dataset.pageSize, 10);
    const currentPage = parseInt(meta.dataset.currentPage, 10);
    const totalPages = parseInt(meta.dataset.totalPages, 10);
    const { sectionId, featuredProductId } = meta.dataset;
    const hasImageTile = meta.dataset.featuredImage === 'true';

    // Claimed up front so a mutation raised by our own appends cannot re-enter.
    meta.dataset.filled = 'true';

    // A single raw page is already the whole collection.
    if (!pageSize || !currentPage || !(totalPages > 1)) {
      const visible = parseInt(meta.dataset.visibleCount, 10);
      if (totalPages === 1 && !Number.isNaN(visible)) this.updateResultCount(visible);
      return;
    }

    // A filter swap mid-fill starts a new run; the old one must not touch it.
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.wrapper.setAttribute('aria-busy', 'true');
    this.setPaginationVisible(false);

    try {
      const order = await this.loadProductOrder({ pageSize, totalPages, featuredProductId, signal });

      // Without the index the raw page and Shopify's own pagination still work.
      if (!order || signal.aborted) return;

      const firstOnPage = this.firstIndexByPage(order);
      const skip = Math.max(parseInt(new URL(window.location.href).searchParams.get('skip'), 10) || 0, 0);
      const start = this.startIndex(order, firstOnPage, currentPage) + skip;

      const pages = this.layoutPages(order.length, {
        pageSize,
        hasImageTile,
        bannerPositions: this.liveBannerPositions(grid),
      });
      const pageCount = pages.length;
      const pageIndex = this.pageIndexFor(pages, start);
      const page = pages[pageIndex];
      const pageNumber = pageIndex + 1;
      const pageUrl = (number) => this.pageUrl(order, firstOnPage, pages[number - 1].first);

      await this.renderSlice({
        grid,
        slice: page.indices.map((index) => order[index]),
        currentPage,
        sectionId,
        isFirstPage: pageIndex === 0,
        signal,
      });
      if (signal.aborted) return;

      // Banners are slotted relative to the first card this page shows.
      meta.dataset.currentOffset = String(page.startSlot);

      this.renderNextTile(pageNumber < pageCount ? pageUrl(pageNumber + 1) : null);
      this.renderPagination(pageNumber, pageCount, pageUrl);
      this.updateResultCount(order.length);
    } finally {
      if (!signal.aborted) {
        this.wrapper.removeAttribute('aria-busy');
        this.setPaginationVisible(true);
      }
    }

    if (signal.aborted) return;

    // Wishlist buttons are injected by a document-wide pass, so appended cards
    // need it re-run. Lazysizes and quick view both pick up new nodes on their
    // own (a MutationObserver and delegated listeners respectively).
    if (typeof window.getTheWishlistItems === 'function') window.getTheWishlistItems();

    document.dispatchEvent(new CustomEvent('collection-grid:updated'));
  }

  /**
   * Every visible product of the collection, in grid order, as { id, page }.
   * Page 1 leads with the featured product and the grid drops its other copy,
   * so it is placed first and skipped wherever it sits in the raw order.
   */
  async loadProductOrder({ pageSize, totalPages, featuredProductId, signal }) {
    const pages = await this.loadIndex({ pageSize, totalPages, signal });
    if (!pages) return null;

    const order = [];
    const seen = new Set();

    if (featuredProductId) {
      order.push({ id: featuredProductId, page: 1 });
      seen.add(featuredProductId);
    }

    pages.forEach((ids, index) => {
      for (const id of ids) {
        if (seen.has(id)) continue;
        seen.add(id);
        order.push({ id, page: index + 1 });
      }
    });

    return order;
  }

  /** Visible product IDs per raw page, cached per collection + filters + sort. */
  async loadIndex({ pageSize, totalPages, signal }) {
    const cacheKey = `${INDEX_CACHE_PREFIX}${this.listingUrl().toString()}`;
    const cached = this.readCache(cacheKey);
    if (cached?.pageSize === pageSize && cached.pages?.length === totalPages) return cached.pages;

    const pages = new Array(totalPages);
    let nextPage = 1;
    let failed = false;

    const worker = async () => {
      while (!failed && nextPage <= totalPages) {
        const page = nextPage;
        nextPage += 1;

        const entry = await this.fetchIndexPage(page, signal);
        if (!entry || entry.pageSize !== pageSize || entry.totalPages !== totalPages) {
          failed = true;
          return;
        }
        pages[page - 1] = entry.ids;
      }
    };

    await Promise.all(Array.from({ length: Math.min(INDEX_FETCH_CONCURRENCY, totalPages) }, worker));
    if (failed) return null;

    this.writeCache(cacheKey, { pageSize, pages });
    return pages;
  }

  async fetchIndexPage(page, signal) {
    const url = this.listingUrl();
    url.searchParams.set('section_id', INDEX_SECTION_ID);
    url.searchParams.set('page', String(page));

    const doc = await this.fetchDocument(url, signal);
    const index = doc?.querySelector('[collection-product-index]');
    if (!index) return null;

    return {
      pageSize: parseInt(index.dataset.pageSize, 10),
      totalPages: parseInt(index.dataset.totalPages, 10),
      ids: index.dataset.productIds.split(',').filter(Boolean),
    };
  }

  /** Returns one raw page's product tiles from the grid section, or null. */
  async fetchGridPage(sectionId, page, signal) {
    if (!sectionId) return null;

    const url = this.listingUrl();
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    const doc = await this.fetchDocument(url, signal);
    // Banners are skipped: every page renders the full banner list, so the ones
    // here are duplicates of those already in the grid.
    return doc ? Array.from(doc.querySelectorAll('.collection-listing__items > [data-product-id]')) : null;
  }

  async fetchDocument(url, signal) {
    try {
      const response = await fetch(url.toString(), { signal });
      if (!response.ok) return null;
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      return null;
    }
  }

  /**
   * Leaves exactly the slice's products in the grid: drops the tiles that
   * belong to the previous page and appends the rest from later raw pages.
   */
  async renderSlice({ grid, slice, currentPage, sectionId, isFirstPage, signal }) {
    const wanted = new Set(slice.map(({ id }) => id));
    const tiles = () => Array.from(grid.querySelectorAll(':scope > [data-product-id]'));

    for (const tile of tiles()) {
      if (!wanted.has(tile.dataset.productId)) tile.remove();
    }

    // Raw page 1 can also open a later page (?page=1&skip=N), and only the
    // first page leads with the featured tile and its enlarged neighbours.
    if (!isFirstPage) {
      grid.querySelector(':scope > .collection-listing__item--featured:not([data-product-id])')?.remove();
      for (const tile of grid.querySelectorAll(':scope > .collection-listing__item--featured-alt')) {
        tile.classList.remove('collection-listing__item--featured-alt');
      }
    }

    const laterPages = [...new Set(slice.map(({ page }) => page).filter((page) => page > currentPage))];
    const fetched = await Promise.all(laterPages.map((page) => this.fetchGridPage(sectionId, page, signal)));
    if (signal.aborted) return;

    const available = new Map();
    for (const tile of fetched.flat()) {
      if (tile && !available.has(tile.dataset.productId)) available.set(tile.dataset.productId, tile);
    }

    const present = new Set(tiles().map((tile) => tile.dataset.productId));
    const nextPageTile = grid.querySelector('.collection-listing__item--next-page');

    for (const { id } of slice) {
      if (present.has(id) || !available.has(id)) continue;
      grid.insertBefore(document.importNode(available.get(id), true), nextPageTile);
      present.add(id);
    }
  }

  /**
   * Cuts the collection into pages of pageSize cards, where a card is a
   * product, the image featured tile or a live promotion banner. Banners hold
   * their configured slot (1 = first card of the collection) and the products,
   * led by the image tile on page 1, flow around them — the same rule
   * plp-banner-scheduler.js places them by. A page therefore carries fewer
   * products when banners land on it, and Next Page is always card pageSize + 1.
   *
   * Returns [{ startSlot, first, indices }]: the page's first slot, the order
   * index of its first product (null if it has none) and all of them.
   */
  layoutPages(productCount, { pageSize, hasImageTile, bannerPositions }) {
    const pages = [];
    let next = hasImageTile ? -1 : 0;

    for (let slot = 0; next < productCount; slot += 1) {
      const pageIndex = Math.floor(slot / pageSize);
      pages[pageIndex] ??= { startSlot: pageIndex * pageSize, first: null, indices: [] };

      if (bannerPositions.has(slot + 1)) continue;

      if (next >= 0) {
        pages[pageIndex].first ??= next;
        pages[pageIndex].indices.push(next);
      }
      next += 1;
    }

    // A page made of banners alone has nothing to link to; page 1 always stays.
    const kept = pages.filter((page, index) => page && (index === 0 || page.indices.length));
    return kept.length ? kept : [{ startSlot: 0, first: null, indices: [] }];
  }

  /** The page holding this product, or the last page if it is past the end. */
  pageIndexFor(pages, orderIndex) {
    const index = pages.findIndex(({ indices }) => indices.includes(orderIndex));
    return index === -1 ? pages.length - 1 : index;
  }

  /**
   * Slot positions of the banners inside their schedule window right now,
   * judged as plp-banner-scheduler.js judges them. Banners shown on one
   * breakpoint only still count, so a page link opens the same products on
   * every device.
   */
  liveBannerPositions(grid) {
    const now = Date.now();
    const time = (value) => {
      const parsed = value ? new Date(value).getTime() : NaN;
      return Number.isNaN(parsed) ? null : parsed;
    };

    const positions = new Set();
    for (const banner of grid.querySelectorAll(':scope > [data-slot-position]')) {
      const start = time(banner.dataset.bannerStart);
      const end = time(banner.dataset.bannerEnd);
      if (start !== null && now < start) continue;
      if (end !== null && now >= end) continue;

      const position = parseInt(banner.dataset.slotPosition, 10);
      if (position > 0) positions.add(position);
    }
    return positions;
  }

  /** Index in the order of each raw page's first visible product. */
  firstIndexByPage(order) {
    const firstOnPage = new Map();
    order.forEach(({ page }, index) => {
      if (!firstOnPage.has(page)) firstOnPage.set(page, index);
    });
    return firstOnPage;
  }

  /** Where this raw page starts in the order, even if all of it is hidden. */
  startIndex(order, firstOnPage, currentPage) {
    if (firstOnPage.has(currentPage)) return firstOnPage.get(currentPage);
    const later = order.findIndex(({ page }) => page > currentPage);
    return later === -1 ? order.length : later;
  }

  /** The URL of the page starting at this position in the order. */
  pageUrl(order, firstOnPage, index) {
    const url = this.listingUrl();
    const entry = index === null ? null : order[index];

    if (entry && index > 0) {
      const skip = index - firstOnPage.get(entry.page);
      url.searchParams.set('page', String(entry.page));
      if (skip > 0) url.searchParams.set('skip', String(skip));
    }

    return `${url.pathname}${url.search}`;
  }

  /** The current listing — collection, filters and sort — without paging. */
  listingUrl() {
    const url = new URL(window.location.href);
    ['page', 'skip', 'section_id'].forEach((param) => url.searchParams.delete(param));
    url.hash = '';
    return url;
  }

  setPaginationVisible(visible) {
    const nav = this.wrapper.querySelector('.pagination__wrapper');
    const nextTile = this.wrapper.querySelector('.collection-listing__item--next-page');
    [nav, nextTile].forEach((el) => {
      if (el && !el.hasAttribute('pagination-empty')) el.hidden = !visible;
    });
  }

  /** Points the Next Page tile at the following page, or retires it. */
  renderNextTile(url) {
    const nextTile = this.wrapper.querySelector('.collection-listing__item--next-page');
    if (!nextTile) return;

    if (!url) {
      nextTile.remove();
      return;
    }

    const link = nextTile.querySelector('.collection-listing__item-next-page-link');
    if (link) link.href = url;
  }

  /**
   * Rebuilds the numbered links for the split pages from the Liquid template:
   * first, last and a window around the current page, with gaps collapsed.
   * Hidden by attribute rather than removed when there is a single page, so
   * the wrapper's MutationObserver is not tripped.
   */
  renderPagination(pageNumber, pageCount, pageUrl) {
    const nav = this.wrapper.querySelector('.pagination__wrapper');
    const list = nav?.querySelector('.pagination__list');
    const template = this.wrapper.querySelector('[collection-pagination-template]');
    if (!nav || !list || !template) return;

    if (pageCount <= 1) {
      nav.setAttribute('pagination-empty', '');
      nav.hidden = true;
      return;
    }

    const part = (name) => template.content.querySelector(`[pagination-part="${name}"]`).cloneNode(true);
    const shown = new Set([1, pageCount]);
    for (let number = pageNumber - PAGINATION_WINDOW; number <= pageNumber + PAGINATION_WINDOW; number += 1) {
      if (number >= 1 && number <= pageCount) shown.add(number);
    }
    const numbers = [...shown].sort((a, b) => a - b);

    const items = [];
    numbers.forEach((number, index) => {
      const gap = number - (numbers[index - 1] ?? number);
      if (gap === 2) items.push({ number: number - 1 });
      else if (gap > 2) items.push({ gap: true });
      items.push({ number });
    });

    const parts = [];
    items.forEach(({ number, gap }, index) => {
      if (gap) {
        parts.push(part('gap'));
        return;
      }

      if (number === pageNumber) {
        const current = part('current');
        current.querySelector('a').textContent = String(number);
        parts.push(current);
        if (index < items.length - 1) parts.push(part('line'));
        return;
      }

      const link = part('link');
      const anchor = link.querySelector('a');
      anchor.textContent = String(number);
      anchor.href = pageUrl(number);
      parts.push(link);
    });

    list.replaceChildren(...parts);
    nav.removeAttribute('pagination-empty');
  }

  updateResultCount(total) {
    const label = `${total} result${total === 1 ? '' : 's'}`;

    const counter = this.wrapper.querySelector('[collection-items-count]');
    if (counter) counter.dataset.count = label;

    // Lives outside the wrapper, so a filter swap leaves it untouched.
    const filterCount = document.querySelector('[collection-sortings-filter-count]');
    if (filterCount) filterCount.textContent = label;
  }

  readCache(key) {
    try {
      const entry = JSON.parse(window.sessionStorage.getItem(key));
      return entry && Date.now() - entry.savedAt < INDEX_CACHE_TTL ? entry : null;
    } catch (error) {
      return null;
    }
  }

  writeCache(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }));
    } catch (error) {
      // Storage full or blocked: the index is simply fetched again next time.
    }
  }
}

if (!customElements.get('collection-grid-fill')) {
  customElements.define('collection-grid-fill', CollectionGridFill);
}
